import { memo, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ChevronDown, LogOut, Settings, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PATHS } from '@/constants/path';
import { PERMISSIONS } from '@/constants/permissions';
import { usePermission } from '@/hooks/usePermission';
import { queryClient } from '@/lib/query';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/authStore';

interface HeaderUserProfileProps {
  className?: string;
  avatarSize?: number;
}

const MENU_ITEM_CLASS =
  'flex h-11 cursor-pointer items-center gap-3 whitespace-nowrap rounded-none px-4 text-foreground hover:bg-[var(--custom-select-hover)]';

export const HeaderUserProfile = memo(function HeaderUserProfile({
  className,
  avatarSize = 32,
}: HeaderUserProfileProps) {
  const navigate = useNavigate();
  const logout = useAuthStore(state => state.logout);
  const user = useAuthStore(state => state.user);
  const { can } = usePermission();
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  // Pre-compute all values to avoid re-renders
  const displayEmail = user?.email || user?.login || '';

  const avatarInitials = useMemo(() => {
    if (!user?.firstName) return 'U';

    const name = user.firstName.trim();
    if (name.includes(' ')) {
      const words = name.split(/\s+/);

      if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
      }
      return words[0].substring(0, 2).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }, [user]);

  const userRole = useMemo(() => {
    if (!user?.roles?.length) return '';
    return user.roles.includes('ROLE_ADMIN')
      ? 'Admin'
      : user.roles[0]?.replace('ROLE_', '');
  }, [user]);

  const showSettings = can(PERMISSIONS.THEME.UPDATE);

  const menuLabels = useMemo(
    () => ({
      profile: t('header.account.accountInfo'),
      settings: t('common.settings'),
      logout: t('common.logout'),
    }),
    [t],
  );

  const handleLogout = useCallback(() => {
    queryClient.clear();
    logout();
    navigate(PATHS.LOGIN);
  }, [logout, navigate]);

  const handleNavigateProfile = useCallback(
    () => navigate(PATHS.ADMIN.PROFILE.ROOT),
    [navigate],
  );

  const handleNavigateSettings = useCallback(
    () => navigate(PATHS.ADMIN.SETTINGS.LIST),
    [navigate],
  );

  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'flex cursor-pointer items-center gap-3 outline-none',
            className,
          )}
        >
          <div
            className="flex-center rounded-full text-white shadow-sm"
            style={{
              backgroundColor: 'var(--custom-cta-bg)',
              width: avatarSize,
              height: avatarSize,
            }}
          >
            <span className="text-base font-bold">{avatarInitials}</span>
          </div>

          <div className="hidden text-left md:block">
            <p
              className="leading-tight font-bold"
              style={{ color: 'var(--custom-text-primary)', fontSize: '12px' }}
            >
              {displayEmail}
            </p>
            <p
              className="leading-tight font-normal"
              style={{
                color: 'var(--custom-text-secondary)',
                fontSize: '10px',
              }}
            >
              {userRole}
            </p>
          </div>

          <ChevronDown
            size={18}
            className={cn(
              'text-muted-foreground hidden transition-transform duration-200 md:block',
              isOpen && 'rotate-180',
            )}
          />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="border-border/50 bg-muted/90 min-w-[180px] overflow-hidden rounded-xl border p-0 shadow-xl backdrop-blur-[4px]"
      >
        <DropdownMenuItem
          onClick={handleNavigateProfile}
          className={MENU_ITEM_CLASS}
        >
          <User className="text-muted-foreground h-5 w-5 shrink-0" />
          <span className="text-sm">{menuLabels.profile}</span>
        </DropdownMenuItem>

        {showSettings && (
          <DropdownMenuItem
            onClick={handleNavigateSettings}
            className={MENU_ITEM_CLASS}
          >
            <Settings className="text-muted-foreground h-5 w-5 shrink-0" />
            <span className="text-sm">{menuLabels.settings}</span>
          </DropdownMenuItem>
        )}

        <DropdownMenuItem
          onClick={handleLogout}
          className="border-border/50 text-destructive hover:bg-destructive/10 hover:text-destructive focus:bg-destructive/10 focus:text-destructive flex h-11 cursor-pointer items-center gap-3 rounded-none border-t px-4 whitespace-nowrap"
        >
          <LogOut className="text-destructive h-5 w-5 shrink-0" />
          <span className="text-sm">{menuLabels.logout}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
